<template>
  <div>
    <h3 class="text-size-lg-md weight--heavier layout--margin-bottom-md">
      1. Select a New Plan
    </h3>
    <div
      v-if="hasActivePromotion"
      class="bill__promo-banner layout--margin-bottom-md"
      style="background-color: #4a90e2; color: #fff; padding: 1em 1.5em; border-radius: 6px; text-align: center; font-size: 18px; font-weight: 600; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);"
    >
      &#127881; Black Friday Promotion! Save ${{ promotionAmount }}/month on all
      monthly paid plans for 3 months!<sup
        style="font-size: 0.7em; vertical-align: super;"
        >&dagger;</sup
      >
      &#127881;
    </div>
    <div class="button__toggle-wrap layout--padding-bottom-lg">
      <div class="layout--block">
        <input
          type="radio"
          id="monthly"
          name="payment-plan"
          value="monthly"
          v-model="paymentPlan"
          class="layout--hide"
        />
        <label
          for="monthly"
          class="button button--toggle button--toggle-left layout--text-left"
          :class="{ 'button--toggle-active': paymentPlan === 'monthly' }"
        >
          <span
            class="input-group__radio-icon"
            :class="{
              'input-group__radio-icon--active': paymentPlan !== 'annual',
            }"
          />
          Bill Monthly
        </label>
      </div>
      <div
        class="layout--flex-column layout--flex-align-start layout--text-center"
      >
        <input
          type="radio"
          id="annual"
          name="payment-plan"
          value="annual"
          v-model="paymentPlan"
          class="layout--hide"
        />
        <label
          for="annual"
          class="button button--toggle button--toggle-right layout--text-left"
          :class="{ 'button--toggle-active': paymentPlan === 'annual' }"
        >
          <span
            class="input-group__radio-icon"
            :class="{
              'input-group__radio-icon--active': paymentPlan === 'annual',
            }"
          />
          Bill Annually <i>(Save 20%)</i></label
        >
      </div>
    </div>
    <ul class="account__plan-choice-list layout--margin-bottom-lg">
      <li
        v-for="option in offeredSortedSubscriptionOptions"
        :key="option.subscriptionType"
        class="account__plan-choice-list-item"
        :class="{
          'account__plan-choice-list-item--inactive':
            option.subscriptionType === subscription.subscriptionType,
        }"
      >
        <h4 class="text-size-sm layout--margin-bottom-xs">
          {{ option.label }}
        </h4>
        <p class="text-size-xl weight--bold layout--margin-bottom-xxs">
          {{ option.monthlyCredits | abbreviateMillion }}
        </p>
        <p>
          <IconEyeGreen
            class="icon icon--medium layout--margin-right-xxs"
          />Monthly Views
        </p>
        <Divider dark class="layout--margin-top-md layout--margin-bottom-md" />
        <div v-if="hasPromotion(option)">
          <p
            class="text-size-md"
            style="text-decoration: line-through; color: #e74c3c; font-size: 16px; margin-bottom: 0.5em;"
          >
            {{ getOriginalPriceLabel(option) }}
          </p>
          <p class="text-size-md" style="color: #27ae60; font-weight: 700;">
            {{ getPriceLabel(option) }}
          </p>
        </div>
        <p v-else class="text-size-md">
          {{ getPriceLabel(option) }}
        </p>
        <Divider dark class="layout--margin-top-md layout--margin-bottom-md" />
        <ul class="account__plan-feature-list">
          <li
            class="account__plan-feature-list-item account__plan-feature-list-item--active"
          >
            Unlimited Timers
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active': !option.hasBranding,
            }"
          >
            Branding removed
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active': option.hasDynamicLocal,
            }"
          >
            Dynamic Language
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active':
                option.costPerCredit > 0,
            }"
          >
            Additional views<br />
            <i>
              {{
                option.costPerCredit > 0
                  ? "($" + option.costPerCredit + "/view)"
                  : "None"
              }}</i
            >
          </li>
          <li
            class="account__plan-feature-list-item"
            :class="{
              'account__plan-feature-list-item--active': option.hasRollover,
            }"
          >
            Credits rollover
          </li>
        </ul>
        <p
          v-if="option.subscriptionType === subscription.subscriptionType"
          class="account__plan-current"
        >
          Your Current Plan
        </p>
        <router-link
          v-if="
            typeof subscription.subscriptionType !== 'undefined' &&
              subscription.subscriptionType !== option.subscriptionType &&
              option.subscriptionType !== 0
          "
          :to="{
            name: 'choose billing period',
            params: { subscriptionType: option.subscriptionType },
          }"
          type="button"
          class="button button--fixed-width button--no-x-padding layout--block layout--margin-top-lg"
          >Select {{ option.label }}</router-link
        >
        <router-link
          v-if="
            typeof subscription.subscriptionType !== 'undefined' &&
              subscription.subscriptionType !== option.subscriptionType &&
              option.subscriptionType === 0 &&
              !(isEarlyBird || isVip)
          "
          :to="{
            name: 'summary',
            params: { subscriptionType: 0, billingPeriod: 0 },
          }"
          type="button"
          class="button button--fixed-width button--no-x-padding layout--block layout--margin-top-lg"
          >Select {{ option.label }}</router-link
        >
        <button
          v-if="
            typeof subscription.subscriptionType !== 'undefined' &&
              subscription.subscriptionType !== option.subscriptionType &&
              option.subscriptionType === 0 &&
              (isEarlyBird || isVip)
          "
          disabled
          type="button"
          class="button button--fixed-width button--no-x-padding layout--block layout--margin-top-lg"
        >
          Not Available
        </button>
      </li>
    </ul>
    <p class="weight--light">
      Prices are charged on a recurring monthly or annual basis. Unused credits
      rollover into the following month for specific plans.<br />* Per
      impression overage rates apply to all timers rendered over monthly plan
      credits. Minimum overage fee is $1.<br />
      ** Rollover credits cannot be transfered between plans.<span
        v-if="hasActivePromotion"
        ><br />&dagger; Save ${{ promotionAmount }} a month for 3 months. Cancel
        anytime. After 3 months, prices return to normal plan pricing.</span
      >
    </p>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

import Divider from "../../../components/Divider";

import IconEyeGreen from "../../../assets/icons/iconImpressionsEye-Green.svg";

export default {
  name: "SelectPlan",
  components: { IconEyeGreen, Divider },
  mounted: function() {
    if (this.sortedSubscriptionOptions.length === 0) {
      this.getSubscriptionOptions();
    }
  },
  data() {
    return {
      paymentPlan: "monthly",
      // Expected prices without promotion (as defined in SubscriptionMailWayInfo)
      // Note: subscriptionType IDs from API are: Bootstrap=5, Startup=4, Enterprise=3
      expectedPrices: {
        5: 10, // Bootstrap (subscriptionType 5 in API)
        4: 60, // Startup (subscriptionType 4 in API)
        3: 200, // Enterprise (subscriptionType 3 in API)
      },
    };
  },
  methods: {
    ...mapActions("account", ["getSubscriptionOptions"]),
    /**
     * Checks if a plan has promotional pricing (non-free plans with monthly billing)
     */
    hasPromotion: function(option) {
      // Show promotion only for paid plans when viewing monthly prices
      if (this.paymentPlan !== "monthly" || option.subscriptionType === 0) {
        return false;
      }

      const displayedPrice = parseFloat(
        option.monthlyPriceLabel.replace(/[^0-9.]/g, "")
      );
      const expectedPrice = this.expectedPrices[option.subscriptionType];

      // Has promotion if displayed price is less than expected price
      return expectedPrice && displayedPrice < expectedPrice;
    },
    /**
     * Returns the original price before promotion
     */
    getOriginalPriceLabel: function(option) {
      const expectedPrice = this.expectedPrices[option.subscriptionType];

      if (this.paymentPlan === "annual") {
        return option.annualPriceLabel + "/year";
      } else {
        return "$" + expectedPrice.toFixed(2) + "/month";
      }
    },
    /**
     * Returns the correct monthly / annual price with label at the end unless
     * it's a free account, then there's no label at the end
     */
    getPriceLabel: function(option) {
      let priceLabel = "";
      let paymentPlanLabel = "";
      if (this.paymentPlan === "annual") {
        priceLabel = option.annualPriceLabel;
        paymentPlanLabel = "/year";
      } else {
        priceLabel = option.monthlyPriceLabel;
        paymentPlanLabel = "/month";
      }
      if (option.subscriptionType !== 0) {
        return priceLabel + paymentPlanLabel;
      }
      return priceLabel;
    },
  },
  computed: {
    ...mapGetters("account", [
      "sortedSubscriptionOptions",
      "subscription",
      "averageImpressions",
    ]),
    offeredSortedSubscriptionOptions() {
      return this.sortedSubscriptionOptions.filter(sub => sub.offered);
    },
    isEarlyBird() {
      return this.subscription.subscriptionType === 1;
    },
    isVip() {
      return this.subscription.subscriptionType === 2;
    },
    /**
     * Checks if there's an active promotion by comparing any plan's price
     */
    hasActivePromotion() {
      if (this.sortedSubscriptionOptions.length === 0) {
        return false;
      }

      // Single debug point to detect promotion status issues
      const Bootstrap = this.sortedSubscriptionOptions.find(
        o => o.subscriptionType === 5
      );
      if (Bootstrap) {
        const displayedPrice = parseFloat(
          Bootstrap.monthlyPriceLabel.replace(/[^0-9.]/g, "")
        );
        const expectedPrice = this.expectedPrices[5];
        const hasDiscount = expectedPrice && displayedPrice < expectedPrice;
        console.log("[Promo Check]", {
          plan: "Bootstrap",
          displayed: displayedPrice,
          expected: expectedPrice,
          active: hasDiscount,
          amount: hasDiscount ? expectedPrice - displayedPrice : 0,
        });
      }

      // Check if any paid plan has a discount
      for (let option of this.sortedSubscriptionOptions) {
        if (option.subscriptionType !== 0) {
          const displayedPrice = parseFloat(
            option.monthlyPriceLabel.replace(/[^0-9.]/g, "")
          );
          const expectedPrice = this.expectedPrices[option.subscriptionType];

          if (expectedPrice && displayedPrice < expectedPrice) {
            return true;
          }
        }
      }
      return false;
    },
    /**
     * Gets the promotion amount from the first discounted plan
     */
    promotionAmount() {
      for (let option of this.sortedSubscriptionOptions) {
        if (option.subscriptionType !== 0) {
          const displayedPrice = parseFloat(
            option.monthlyPriceLabel.replace(/[^0-9.]/g, "")
          );
          const expectedPrice = this.expectedPrices[option.subscriptionType];

          if (expectedPrice && displayedPrice < expectedPrice) {
            return (expectedPrice - displayedPrice).toFixed(0);
          }
        }
      }
      return 0;
    },
  },
};
</script>

<style></style>
